import { z } from 'zod';

const toastHistoryItemSchema = z.object({
  id: z.string(),
  toastId: z.string(),
  type: z.enum(['default', 'success', 'error', 'warning', 'info', 'message', 'loading']),
  title: z.string(),
  description: z.string().optional(),
  route: z.string().optional(),
  status: z.enum(['active', 'dismissed']),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type ToastHistoryItem = z.infer<typeof toastHistoryItemSchema>;

export type ToastHistoryType = ToastHistoryItem['type'];

interface RecordToastHistoryInput {
  toastId: string | number;
  type: ToastHistoryType;
  title: string;
  description?: string;
}

const TOAST_HISTORY_LIMIT = 100;

const TOAST_HISTORY_STORAGE_KEY = 'pesto.toast-history.v1';

const TOAST_HISTORY_CHANGED_EVENT = 'pesto:toast-history-changed';

const isBrowser = () => typeof window !== 'undefined';

const runtimeId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const toRuntimeToastId = (toastId: string | number) => `${runtimeId}:${String(toastId)}`;

const createHistoryId = () => {
  if (isBrowser() && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getCurrentRoute = () => {
  if (!isBrowser()) return undefined;

  return window.location.pathname;
};

const dispatchHistoryChanged = () => {
  if (!isBrowser()) return;

  window.dispatchEvent(new Event(TOAST_HISTORY_CHANGED_EVENT));
};

const parseHistory = (value: string | null): ToastHistoryItem[] => {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);

    if (!Array.isArray(parsed)) return [];

    return parsed.flatMap((item) => {
      const result = toastHistoryItemSchema.safeParse(item);

      return result.success ? [result.data] : [];
    });
  } catch {
    return [];
  }
};

const readStorageValue = () => {
  if (!isBrowser()) return null;

  try {
    return window.localStorage.getItem(TOAST_HISTORY_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeToastHistory = (items: ToastHistoryItem[]) => {
  if (!isBrowser()) return;

  try {
    window.localStorage.setItem(TOAST_HISTORY_STORAGE_KEY, JSON.stringify(items.slice(0, TOAST_HISTORY_LIMIT)));
    dispatchHistoryChanged();
  } catch {}
};

export const readToastHistory = (): ToastHistoryItem[] => {
  return parseHistory(readStorageValue());
};

export const recordToastHistory = (input: RecordToastHistoryInput) => {
  const { title, description } = input;

  if (!title) return;

  const now = new Date().toISOString();
  const toastId = toRuntimeToastId(input.toastId);
  const currentItems = readToastHistory();
  const existingItem = currentItems.find((item) => item.toastId === toastId);

  const nextItem: ToastHistoryItem = {
    id: existingItem?.id ?? createHistoryId(),
    toastId,
    type: input.type,
    title,
    description,
    route: getCurrentRoute(),
    status: 'active',
    createdAt: existingItem?.createdAt ?? now,
    updatedAt: now,
  };

  writeToastHistory([nextItem, ...currentItems.filter((item) => item.toastId !== toastId)]);
};

export const markToastHistoryDismissed = (toastId?: string | number) => {
  const currentItems = readToastHistory();
  const now = new Date().toISOString();

  const nextItems = currentItems.map((item) => {
    const shouldDismiss =
      toastId != null ? item.toastId === toRuntimeToastId(toastId) : item.toastId.startsWith(runtimeId);

    if (!shouldDismiss) return item;

    return {
      ...item,
      status: 'dismissed' as const,
      updatedAt: now,
    };
  });

  writeToastHistory(nextItems);
};

export const clearToastHistory = () => {
  if (!isBrowser()) return;

  try {
    window.localStorage.removeItem(TOAST_HISTORY_STORAGE_KEY);
    dispatchHistoryChanged();
  } catch {}
};

export const subscribeToToastHistory = (listener: () => void) => {
  if (!isBrowser()) return () => {};

  const handleStorage = (event: StorageEvent) => {
    if (event.key === TOAST_HISTORY_STORAGE_KEY) {
      listener();
    }
  };

  window.addEventListener(TOAST_HISTORY_CHANGED_EVENT, listener);
  window.addEventListener('storage', handleStorage);

  return () => {
    window.removeEventListener(TOAST_HISTORY_CHANGED_EVENT, listener);
    window.removeEventListener('storage', handleStorage);
  };
};
